import React, { useEffect, useState } from 'react';

// ==========================================================================
// SUBCOMPONENTE: COLUMNA DE JUGADOR
// Maneja su propia lógica de animación para el plato sin afectar al rival
// ==========================================================================
const PlayerColumn = ({ playerName, playerStats, avatarImg, isLeftColumn }) => {
    const currentStep = playerStats?.currentStep || 1;
    const totalTacos = playerStats?.tacosCompleted || 0;

    // Estados para controlar la animación del plato
    const [plateCount, setPlateCount] = useState(0);
    const [isAnimatingPlate, setIsAnimatingPlate] = useState(false);
    const [pulseScore, setPulseScore] = useState(false);

    const ASSETS = {
        tacoBuilder: (step) => `/img/games/taco/barrio_9/${step}.png`,
        plate: (count) => `/img/games/taco/barrio_9/box-${count}.png` // Asegúrate de tener plato-0.png hasta plato-5.png
    };

    // Efecto para vigilar cuando el jugador suma un taco
    useEffect(() => {
        // Hacemos latir el marcador gigante
        setPulseScore(true);
        setTimeout(() => setPulseScore(false), 300);

        // Lógica del plato
        const remainder = totalTacos % 5;

        if (totalTacos > 0 && remainder === 0) {
            // Acaba de completar un múltiplo de 5. Ponemos la imagen en 5 y animamos la salida.
            setPlateCount(5);
            setIsAnimatingPlate(true);

            // Medio segundo después, regresamos el plato vacío
            const timer = setTimeout(() => {
                setIsAnimatingPlate(false);
                setPlateCount(0);
            }, 600); 

            return () => clearTimeout(timer);
        } else {
            // Si no es múltiplo de 5, simplemente mostramos el progreso actual
            setPlateCount(remainder);
            setIsAnimatingPlate(false);
        }
    }, [totalTacos]);

    return (
        <div className="taco-screen__player-column">
            
            {/* 1. EL AVATAR */}
            <div className="taco-screen__avatar-container">
                <img src={avatarImg} alt={playerName} className="taco-screen__avatar" />
                <h2 className="taco-screen__name">{playerName}</h2>
            </div>

            {/* 2. TACO EN CONSTRUCCIÓN (Ingredientes) */}
            <div className="taco-screen__builder">
                <img 
                    src={ASSETS.tacoBuilder(currentStep)} 
                    alt={`Paso ${currentStep}`} 
                    className="taco-screen__taco-img" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* 3. MARCADOR GIGANTE */}
            <div className="taco-screen__score-box">
                <p className={`taco-screen__score-number ${pulseScore ? 'taco-screen__score-number--pulse' : ''}`}>
                    {totalTacos}
                </p>
                <p className="taco-screen__score-label">Paquetes</p>
            </div>

            {/* 4. EL PLATO QUE SE LLENA */}
            <div className="taco-screen__plate-container">
                <img 
                    src={ASSETS.plate(plateCount)} 
                    alt={`Plato con ${plateCount} tacos`} 
                    className={`taco-screen__plate-img ${isAnimatingPlate ? (isLeftColumn ? 'taco-screen__plate-img--slide-left' : 'taco-screen__plate-img--slide-right') : ''}`} 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

        </div>
    );
};


// ==========================================================================
// COMPONENTE PRINCIPAL DE PANTALLA GIGANTE
// ==========================================================================
const Package = ({ currentGameState }) => {
    
    // Extracción segura de la data inyectada por Screen.js
    const {
        status = 0, // 0: Waiting, 1: Running, 2: Finished
        winMode = 0,
        durationSeconds = 20,
        player1 = null,
        player2 = null,
        winner = 'none'
    } = currentGameState || {};

    const [timeLeft, setTimeLeft] = useState(durationSeconds);

    // Reloj Local de la Pantalla (Sincronizado)
    useEffect(() => {
        let timer;
        // Si arranca un juego nuevo, reseteamos el reloj
        if (status === 1) {
            setLocalTimeLeftSafe();
            
            // Solo corremos el reloj si el modo de victoria es por Tiempo (0)
            if (winMode === 0) {
                timer = setInterval(() => {
                    setTimeLeft(prev => Math.max(0, prev - 1));
                }, 1000);
            }
        } else if (status === 0) {
            setTimeLeft(durationSeconds); // Espera: muestra el tiempo inicial
        }
        
        return () => clearInterval(timer);
    }, [status, durationSeconds, winMode]);

    const setLocalTimeLeftSafe = () => {
        setTimeLeft(prev => prev === 0 ? durationSeconds : prev);
    }

    // Traductor de mensajes finales
    const getWinnerText = () => {
        if (winner === 'tie') return '¡ES UN EMPATE!';
        if (winner === 'player1') return '¡GANÓ LUCHO!';
        if (winner === 'player2') return '¡GANÓ TUNITA!';
        return '';
    };

    return (
        <div className="taco-screen">
            <div className="taco-screen__overlay-bg"></div>

            {/* RELOJ CENTRAL */}
            {winMode === 0 && (
                <div className="taco-screen__hud">
                    <img src='/img/page/adsl-five.png' style={{ width: '350px' }} />
                    <p className="taco-screen__hud-title">Tiempo</p>
                    <p className="taco-screen__hud-time">00:{timeLeft.toString().padStart(2, '0')}</p>
                </div>
            )}
            
            {winMode === 1 && (
                <div className="taco-screen__hud">
                    <p className="taco-screen__hud-title text-danger">Muerte Súbita</p>
                    <p className="taco-screen__hud-time" style={{ fontSize: '2rem' }}>¡El Primero en llegar!</p>
                </div>
            )}

            {/* ARENA 1 VS 1 */}
            <div className="taco-screen__arena">
                
                {/* JUGADOR 1 (Izquierda) */}
                <PlayerColumn 
                    playerName="Lucho" 
                    playerStats={player1} 
                    avatarImg="/img/games/taco/barrio_9/lucho-empacador.png" 
                    isLeftColumn={true}
                />

                {/* JUGADOR 2 (Derecha) */}
                <PlayerColumn 
                    playerName="Tunita" 
                    playerStats={player2} 
                    avatarImg="/img/games/taco/barrio_9/tunita-empacadora.png" 
                    isLeftColumn={false}
                />

            </div>

            {/* BANNER DE VICTORIA GIGANTE */}
            {status === 2 && winner !== 'none' && (
                <div className="taco-screen__winner-banner">
                    <h1>{getWinnerText()}</h1>
                </div>
            )}

            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', fontSize: '30px', color: '#fff', zIndex: '10' }}>Hecho con ♥ por Interaktia | 2026</p>
        </div>
    );
};

export { Package };